import type { LogsInitConfiguration } from '@datadog/browser-logs';
import type { RumInitConfiguration } from '@datadog/browser-rum';

import {
  appVersion,
  viteApiBaseUrl,
  viteAppEnv,
  viteWarehousingApiBaseUrl,
} from '@montugroup/circuit-shared/constants/env';
import {
  ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY,
  ALT_CUSTOMER_SERVICE_NUMBER_RAW,
} from '@montugroup/frontend-common/constants';

const API_BASE_URL = viteApiBaseUrl();
const WAREHOUSING_API_URL = viteWarehousingApiBaseUrl();

type CalendarConfig = {
  calOrigin: string;
  doctorTeamPath: string;
  embedJsUrl: string;
};

export type CommonDataDogConfig = Pick<LogsInitConfiguration, 'clientToken' | 'site' | 'version'>;
export type EnvSpecificDataDogConfig = Pick<LogsInitConfiguration, 'env'> &
  Pick<
    RumInitConfiguration,
    'sessionSampleRate' | 'sessionReplaySampleRate' | 'allowedTracingUrls' | 'defaultPrivacyLevel'
  >;
export type DataDogConfig = Partial<CommonDataDogConfig> & EnvSpecificDataDogConfig;

type CommonSettings = {
  assetsUrl: string;
  ausPostTrackingUrl: string;
  followUpGoogleDoc: string;
  GoogleAuthId: string;
  googleTagManagerId: string;
  paymentGatwayEnable: number;
  recaptchaSiteKey: string;
  calendarConfig: Pick<CalendarConfig, 'doctorTeamPath'>;
  dataDog: CommonDataDogConfig;
  creditType: {
    OrderIssue: string;
    ProductDefect: string;
    ReScriptCredit: string;
    Other: string;
    Promotion: string;
    Referral: string;
  };
  discountReason: {
    EXTENDED_OUT_OF_STOCK: number;
    POOR_SUPPORT_EXPERIENCE: number;
    DELAYED_SHIPPING: number;
    MATCH_ANOTHER_SUPPLIER: number;
    PROMOTION: number;
    OTHER: number;
    CONCESSION_CARD_HOLDER: number;
  };
  discountType: {
    FULL_ORDER: number;
    DISPENSING_FEE: number;
    MEDICATION_SPECIFIC: number;
  };
  inventoryStatus: {
    INCREMENT: string;
    DECREMENT: string;
  };
  orderStatus: {
    AWAITING_PAYMENT: number;
    PAID_NOT_APPROVED: number;
    APPROVED_NOT_PAID: number;
    AWAITING_SHIPMENT: number;
    SHIPPED: number;
    COMPLETED: number;
    CANCELLED: number;
    PENDING: number;
    TEST: number;
    ERROR: number;
    EXPIRED: number;
    PMS_DELETED: number;
    OUT_OF_REPEATS: number;
    AWAITING_DISPENSE: number;
    PHARMACY_DISPENSED: number;
    PHARMACY_OUT_OF_STOCK: number;
    PHARMACY_COMPLETED: number;
    PHARMACY_PENDING: number;
    PHARMACY_TEST: number;
    INTERVAL_TIME: number;
    NO_PHARMACY: number;
    PHARMACY_PARTIALLY_OUT_OF_STOCK: number;
    OUT_OF_STOCK_REASONING: number;
    RE_SCRIPT: number;
    OUT_OF_STOCK: number;
    PARTIALLY_OUT_OF_STOCK: number;
    AWAITING_TOKEN: number;
    UNABLE_TO_DISPENSE: number;
    PROCESSING_DISPENSE: number;
    PMS_PATIENT_PROFILE_INCOMPLETE: number;
    REFUNDED: number;
  };
  screenResolution: {
    small: number;
    medium: number;
    large: number;
    extralarge: number;
    tablet: number;
  };
  shipmentCarrier: {
    ausPost: string;
    shippit: string;
  };
  transactionType: {
    CREDIT_CARD: number;
    BANK: number;
  };
  bankAccount: {
    name: string;
    bsb: string;
    accountNumber: string;
  };
  support: {
    phone: {
      raw: string;
      display: string;
    };
    email: string;
  };
  marketing: {
    site: {
      url: string;
    };
  };
  productStatus: {
    active: string;
    inactive: string;
  };
};

type EnvSpecificSettings = {
  baseUrl: string;
  brazeApiKey: string;
  brazeBaseUrl: string;
  calendarConfig: CalendarConfig;
  datadog?: DataDogConfig;
  FeatureFlagSDKKey: string;
  googleTagManagerAuth: string;
  googlePlacesApiKey: string;
  productImageUrl: string;
  url: string;
  warehousingApiUrl: string;
  adminApiUrl: string;
  pharmacyApiUrl: string;
  customerPortalApiUrl: string;
  launchDarklyConfig: {
    clientSideId: string;
  };
  shippitTrackingUrl: string;
  shopifyDiscountsURL: string;
  shopifyOrderDiscountAppURL: string;
  shopifyProductDiscountAppURL: string;
  shopifyShippingDiscountAppURL: string;
  zendeskSupportURL: string;
};

export type EnvSettings = CommonSettings & EnvSpecificSettings;

const common: CommonSettings = {
  screenResolution: {
    small: 576,
    medium: 720,
    tablet: 820,
    large: 960,
    extralarge: 1140,
  },
  recaptchaSiteKey: '6LfxAoMhAAAAADy4Bv_Lz6AhzsTTzf7N2cjlICeZ',
  GoogleAuthId: '413997005731-dicel6reedvvm83fscbgvvhccbevfiib.apps.googleusercontent.com',
  creditType: {
    OrderIssue: '1',
    ProductDefect: '2',
    ReScriptCredit: '3',
    Other: '4',
    Promotion: '5',
    Referral: '6',
  },
  discountType: {
    FULL_ORDER: 1,
    DISPENSING_FEE: 2,
    MEDICATION_SPECIFIC: 3,
  },
  discountReason: {
    EXTENDED_OUT_OF_STOCK: 1,
    POOR_SUPPORT_EXPERIENCE: 2,
    DELAYED_SHIPPING: 3,
    MATCH_ANOTHER_SUPPLIER: 4,
    PROMOTION: 5,
    OTHER: 6,
    CONCESSION_CARD_HOLDER: 7,
  },
  transactionType: {
    CREDIT_CARD: 1,
    BANK: 2,
  },
  bankAccount: {
    name: 'Montu Group',
    bsb: '063 097',
    accountNumber: '3229 8127',
  },
  inventoryStatus: {
    INCREMENT: '1',
    DECREMENT: '2',
  },
  orderStatus: {
    AWAITING_PAYMENT: 1,
    PAID_NOT_APPROVED: 2,
    APPROVED_NOT_PAID: 3,
    AWAITING_SHIPMENT: 4,
    SHIPPED: 5,
    /** @deprecated */
    COMPLETED: 6,
    CANCELLED: 7,
    /** @deprecated */
    PENDING: 8,
    TEST: 9,
    ERROR: 10,
    EXPIRED: 11,
    PMS_DELETED: 12,
    OUT_OF_REPEATS: 13,
    AWAITING_DISPENSE: 14,
    PHARMACY_DISPENSED: 15,
    PHARMACY_OUT_OF_STOCK: 16,
    /** @deprecated */
    PHARMACY_COMPLETED: 17,
    /** @deprecated */
    PHARMACY_PENDING: 18,
    PHARMACY_TEST: 19,
    INTERVAL_TIME: 20,
    /** @deprecated */
    NO_PHARMACY: 21,
    PHARMACY_PARTIALLY_OUT_OF_STOCK: 22,
    OUT_OF_STOCK_REASONING: 23,
    RE_SCRIPT: 24,
    /** @deprecated */
    OUT_OF_STOCK: 25,
    PARTIALLY_OUT_OF_STOCK: 26,
    AWAITING_TOKEN: 27,
    UNABLE_TO_DISPENSE: 28,
    PROCESSING_DISPENSE: 29,
    PMS_PATIENT_PROFILE_INCOMPLETE: 30,
    REFUNDED: 31,
  },
  calendarConfig: {
    doctorTeamPath: 'team/alternaleaf-doctors',
  },
  dataDog: {
    clientToken: 'pubbc48eee0cbeb1f3ac5db6d8bd99c0f3b',
    site: 'us3.datadoghq.com',
    version: appVersion(),
  },
  paymentGatwayEnable: 1,
  shipmentCarrier: {
    ausPost: 'AUSPOST',
    shippit: 'SHIPPIT',
  },
  ausPostTrackingUrl: 'https://auspost.com.au/mypost/track/#/details/',
  assetsUrl: `${window.location.origin}/assets/images/UI/`,
  googleTagManagerId: 'GTM-T4XGM9M9',
  followUpGoogleDoc:
    'https://docs.google.com/forms/d/e/1FAIpQLSdpOvjYLOGjAu8AmSBnoftrI3AC6zDdIg4ifbTapw-yjqCrpg/viewform',
  support: {
    phone: {
      raw: ALT_CUSTOMER_SERVICE_NUMBER_RAW,
      display: ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY,
    },
    email: 'pharmacy-escalations@montu.com.au',
  },
  marketing: {
    site: {
      url: 'https://www.montu.com.au/pharmacy',
    },
  },
  productStatus: {
    active: 'Active',
    inactive: 'Inactive',
  },
};

const development: EnvSettings = {
  baseUrl: API_BASE_URL,
  url: `${API_BASE_URL}/api/v1`,
  warehousingApiUrl: WAREHOUSING_API_URL,
  adminApiUrl: `${API_BASE_URL}/api/admin/v1`,
  pharmacyApiUrl: `${API_BASE_URL}/api/pharmacy/v1`,
  customerPortalApiUrl: `${API_BASE_URL}/api/customer-portal/v1`,
  productImageUrl: `${window.location.origin}/assets/images/products/`,
  ...common,
  FeatureFlagSDKKey: 'be49d7b2-589d-4bab-a85b-cca03b9e2f40',
  brazeApiKey: '58efb523-0f51-4b1e-988f-cac946b97dfe',
  brazeBaseUrl: 'sdk.iad-02.braze.com',
  datadog: {
    ...common.dataDog,
    env: 'local',
    sessionSampleRate: 0, // percentage of sessions capturing RUM
    sessionReplaySampleRate: 0, // percentage of sessions capturing RUM, AND session replays
    /*
     * If PII is discovered during a session replay, adding the data attribute data-dd-privacy="mask" will override specific elements
     * See: https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/#override-an-html-element
     */
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [`${API_BASE_URL}/api`],
  },
  calendarConfig: {
    ...common.calendarConfig,
    calOrigin: 'https://bookings.staging.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.staging.alternaleaf.com.au/embed/embed.js',
  },
  launchDarklyConfig: {
    clientSideId: '64867d27a3414f12de1f7416',
  },
  googleTagManagerAuth: '',
  googlePlacesApiKey: 'AIzaSyBbGoHlNxg-31cc-IV1yXQySQRp69TqtBA',
  shippitTrackingUrl: 'https://app.staging.shippit.com/tracking/',
  shopifyDiscountsURL: 'https://admin.shopify.com/store/b2cteststore',
  shopifyOrderDiscountAppURL:
    'apps/circuit-discounts-dev-for-wx/app/order-discount/bd55ebfe-c451-410a-8919-bd17b494be15',
  shopifyProductDiscountAppURL:
    'apps/circuit-discounts-dev-for-wx/app/product-discount/9b704a7d-23b9-44d2-8085-a9d5ed015f2f',
  shopifyShippingDiscountAppURL:
    'apps/circuit-discounts-dev-for-wx/app/shipping-discount/ebd0f507-b5ad-4df0-9c3a-dcebbd14bbc5',
  zendeskSupportURL: 'https://umeds.pharmacy-partner.com/hc/en-au',
};

const staging: EnvSettings = {
  baseUrl: API_BASE_URL,
  url: `${API_BASE_URL}/api/v1`,
  warehousingApiUrl: WAREHOUSING_API_URL,
  adminApiUrl: `${API_BASE_URL}/api/admin/v1`,
  pharmacyApiUrl: `${API_BASE_URL}/api/pharmacy/v1`,
  customerPortalApiUrl: `${API_BASE_URL}/api/customer-portal/v1`,
  productImageUrl: `${window.location.origin}/assets/images/products/`,
  ...common,
  assetsUrl: `${window.location.origin}/assets/images/UI/`,
  FeatureFlagSDKKey: '28a87403-6fa4-4df4-8bcf-aaae0beae867',
  brazeApiKey: '58efb523-0f51-4b1e-988f-cac946b97dfe',
  brazeBaseUrl: 'sdk.iad-02.braze.com',
  datadog: {
    ...common.dataDog,
    env: 'staging',
    sessionSampleRate: 100, // percentage of sessions capturing RUM
    sessionReplaySampleRate: 0, // percentage of sessions capturing RUM, AND session replays
    /*
     * If PII is discovered during a session replay, adding the data attribute data-dd-privacy="mask" will override specific elements
     * See: https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/#override-an-html-element
     */
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [`${API_BASE_URL}/api`],
  },
  calendarConfig: {
    ...common.calendarConfig,
    calOrigin: 'https://bookings.staging.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.staging.alternaleaf.com.au/embed/embed.js',
  },
  launchDarklyConfig: {
    clientSideId: '64867d1f3e893d129b01ff08',
  },
  googleTagManagerAuth: 'JLAVcHtDFWX-1j6KrADSiw',
  googlePlacesApiKey: 'AIzaSyBbGoHlNxg-31cc-IV1yXQySQRp69TqtBA',
  shippitTrackingUrl: 'https://app.staging.shippit.com/tracking/',
  shopifyDiscountsURL: 'https://admin.shopify.com/store/b2cstaging',
  shopifyOrderDiscountAppURL: 'apps/circuit-discounts-staging/app/order-discount/d08a4444-efe1-49e5-ab26-860bb6ba0ba9',
  shopifyProductDiscountAppURL:
    'apps/circuit-discounts-staging/app/product-discount/bb0c61a3-370c-464f-acbb-0610671d3500',
  shopifyShippingDiscountAppURL:
    'apps/circuit-discounts-staging/app/shipping-discount/e7535004-3491-42c5-8755-bc0ed760fb55',
  zendeskSupportURL: 'https://pharmacy-partner1695171906.zendesk.com/hc/en-au',
};

const prod: EnvSettings = {
  baseUrl: API_BASE_URL,
  warehousingApiUrl: WAREHOUSING_API_URL,
  url: `${API_BASE_URL}/api/v1`,
  adminApiUrl: `${API_BASE_URL}/api/admin/v1`,
  pharmacyApiUrl: `${API_BASE_URL}/api/pharmacy/v1`,
  customerPortalApiUrl: `${API_BASE_URL}/api/customer-portal/v1`,
  productImageUrl: `${window.location.origin}/assets/images/products/`,
  ...common,
  assetsUrl: `${window.location.origin}/assets/images/UI/`,
  recaptchaSiteKey: '6LfxpKcaAAAAAMsdrsz9rEZQbk90e4oR1n4y11fe',
  GoogleAuthId: '178327000471-q4dg3ac4j6qudsu1oo277440e8sivter.apps.googleusercontent.com',
  FeatureFlagSDKKey: 'ec1fd7a8-a339-4c56-8ef7-8ae75e30fd97',
  brazeApiKey: 'a1ea623c-bcc7-46e8-a862-36eb5b837fa9',
  brazeBaseUrl: 'sdk.iad-02.braze.com',
  datadog: {
    ...common.dataDog,
    env: 'production',
    sessionSampleRate: 100, // percentage of sessions capturing RUM
    sessionReplaySampleRate: 50, // percentage of sessions capturing RUM, AND session replays. This is being overridden by a feature flag in datadog.ts
    /**
     * If you need to unmask a field, you can use the `data-dd-privacy` attribute
     * data-dd-privacy="allow" will unmask the field if needed for RUM, and isn't PII
     */
    defaultPrivacyLevel: 'mask',
    allowedTracingUrls: [`${API_BASE_URL}/api`],
  },
  calendarConfig: {
    ...common.calendarConfig,
    calOrigin: 'https://bookings.alternaleaf.com.au',
    embedJsUrl: 'https://bookings.alternaleaf.com.au/embed/embed.js',
  },
  launchDarklyConfig: {
    clientSideId: '64867d18418ca9139db03055',
  },
  googleTagManagerAuth: 'cFM07ZbzfdCf3JdRCJxojQ',
  googlePlacesApiKey: 'AIzaSyAhUsEHrULAYUbYq08eYB7asYYBcBHIMIg',
  shippitTrackingUrl: 'https://app.shippit.com/tracking/',
  shopifyDiscountsURL: 'https://admin.shopify.com/store/b2cprod',
  shopifyOrderDiscountAppURL: 'apps/circuit-discounts-prod/app/order-discount/e8ee532e-1bef-46f8-816c-6c6b5b80a08d',
  shopifyProductDiscountAppURL: 'apps/circuit-discounts-prod/app/product-discount/c3ee05e5-4dc5-4169-8264-1c9b9c446b48',
  shopifyShippingDiscountAppURL:
    'apps/circuit-discounts-prod/app/shipping-discount/7210f810-03ad-465d-b3b7-239b5c3ccb95',
  zendeskSupportURL: 'https://umeds.pharmacy-partner.com/hc/en-au',
};

function getEnvironmentSettings(): EnvSettings {
  switch (viteAppEnv()) {
    case 'test':
    case 'development':
      return development;
    case 'staging':
      return staging;
    case 'production':
      return prod;
    default:
      return development;
  }
}

const settings = getEnvironmentSettings();
export default settings;
